"use client";

import Link from "next/link";
import Image from "next/image";

export default function CMGRobotPage() {
  return (
    <main className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-slate-800 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <Link
            href="/"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:bg-slate-900/70"
            aria-label="Back to home"
          >
            <span aria-hidden="true">←</span>
            Back
          </Link>

          <div className="text-xs uppercase tracking-wide text-slate-400">
            Project log
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
        {/* Title / Hero */}
        <section className="space-y-3">
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Control-Moment Stabilising Robot
          </h1>
          <p className="max-w-2xl text-sm text-slate-300 sm:text-base">
            A self-balancing platform that uses two spinning flywheels and rapid
            gimbal changes to generate stabilising torques.
          </p>

          {/* Hero image */}
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="relative aspect-[1/1] w-full">
              <Image
                src="/photos/Gyro.jpeg"
                alt="Control moment stabilising robot on test rig"
                fill
                className="object-cover"
                priority
              />
            </div>
          </div>


        </section>

        {/* Quick facts */}
        <section className="mt-10 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">
              Core idea
            </p>
            <p className="mt-1 text-sm text-slate-200">
              Angular momentum control
            </p>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">
              Actuation
            </p>
            <p className="mt-1 text-sm text-slate-200">
              Flywheel + gimbal servo
            </p>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">
              Control
            </p>
            <p className="mt-1 text-sm text-slate-200">
              IMU feedback + PID tuning
            </p>
          </div>
        </section>

        {/* Blog sections */}
        <section className="mt-12 space-y-10">
          {/* Section 1 */}
          <article className="rounded-2xl border border-slate-800 bg-slate-900/30 p-6">
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="sm:w-2/3">
                <h2 className="text-xl font-semibold tracking-tight">
                  1) What is control-moment stabilisation?
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                    Control-moment stabilisation uses a rapidly spinning flywheel mounted on a gimbal. The flywheel spins at a constant speed, storing angular momentum. When the gimbal rotates, the direction of this angular-momentum vector changes, and this change generates a torque perpendicular to both the flywheel spin axis and the gimbal axis. This torque can be used to counteract disturbances and stabilise the robot as it begins to tip.
                </p>

                <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-slate-300">
                  <li>
                    Flywheel speed sets how much stabilising authority you have
                  </li>
                  <li>
                    Gimbal angle + rate controls the direction and strength of
                    the corrective torque
                  </li>
                  <li>
                    IMU feedback closes the loop (tilt angle + angular rate)
                  </li>
                </ul>
              </div>

              <div className="sm:w-1/3">
                <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
                  {/* Image area */}
                  <div className="aspect-[4/3] w-full bg-slate-950/40">
                    <div className="relative h-full w-full p-3">
                      <Image
                        src="/photos/diagram.png"
                        alt="Control moment gyro logic diagram"
                        fill
                        className="object-contain"
                        sizes="(min-width: 640px) 33vw, 100vw"
                        priority
                      />
                    </div>
                  </div>

                  {/* Caption */}
                  <div className="border-t border-slate-800 p-3">
                    <p className="text-xs text-slate-400">Control Moment Gyro Logic [ResearchGate]</p>
                  </div>
                </div>
              </div>




            </div>
          </article>

          {/* Section 2 */}
          <article className="rounded-2xl border border-slate-800 bg-slate-900/30 p-6">
            <h2 className="text-xl font-semibold tracking-tight">
              2) Mechanical build
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                The frame holds a high-speed flywheel on a gimbal so the rotor axis 
                can be reoriented quickly. Small errors in shaft alignment, bearing seating, or gear mesh add up, and that tolerance 
                 stack-up shows up as misalignment and vibration.This introduces delay between the motor command 
                 and the actual torque applied, which can lead to oscillation and noisy IMU readings.
            </p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
                {/* Media area (this controls size) */}
                <div className="relative aspect-[16/10] w-full bg-slate-950/40">
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute inset-0 h-full w-full object-cover"
                  >
                    <source src="/videos/gearmesh.mp4" type="video/mp4" />
                  </video>
                </div>

                {/* Caption */}
                <div className="border-t border-slate-800 p-3">
                  <p className="text-xs text-slate-400">Gear mesh testing

                  </p>
                </div>
              </div>



              <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
                <div className="aspect-[16/10] w-full bg-slate-950/40" />
              </div>
            </div>
          </article>

          {/* Section 3 */}
          <article className="rounded-2xl border border-slate-800 bg-slate-900/30 p-6">
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="sm:w-1/3">
                <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
                  <div className="aspect-[4/3] w-full bg-slate-950/40" />
                </div>
              </div>

              <div className="sm:w-2/3">
                <h2 className="text-xl font-semibold tracking-tight">
                  3) Sensing and control loop
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
                  The IMU estimates tilt angle and angular velocity. A controller
                  converts that error into a desired gimbal command (angle and/or
                  rate). I’m tuning this to reduce overshoot and stop the “wobble”
                  you get when the response is too aggressive.
                </p>

                <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    Current tuning focus
                  </p>
                  <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-slate-300">
                    <li>Filter IMU noise without adding too much lag</li>
                    <li>Limit gimbal acceleration to avoid exciting vibrations</li>
                    <li>Choose gains that stabilise without oscillation</li>
                  </ul>
                </div>
              </div>
            </div>
          </article>

          {/* Section 4 */}
          <article className="rounded-2xl border border-slate-800 bg-slate-900/30 p-6">
            <h2 className="text-xl font-semibold tracking-tight">
              4) Results and next steps
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">
              The mechanism works and can produce enough torque to make stabilising corrections. I am currently
              tuning the PID controller and troubleshooting as the robot is not self-balancing yet.
            </p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  What worked
                </p>
                <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-slate-300">
                  <li>Higher rotor speed = stronger corrective torque</li>
                  <li>Stiffer mount reduced oscillation</li>
                  <li>Basic PID got first stable “hold”</li>
                </ul>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Next iteration
                </p>
                <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-slate-300">
                  <li>Add saturation limits + anti-windup</li>
                  <li>Improve IMU fusion / filtering</li>
                  <li>Measure response and log data for tuning</li>
                </ul>
              </div>
            </div>
          </article>
        </section>

        {/* Footer nav */}
        <div className="mt-12 flex items-center justify-between">
          <Link
            href="/"
            className="rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:bg-slate-900/70"
          >
            ← Back to home
          </Link>

          <Link
            href="/control"
            className="rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:bg-slate-900/70"
          >
            More projects →
          </Link>
        </div>
      </div>
    </main>
  );
}
